import { getAudioGraphInstance } from "./audioEngine";
import { sendMessageToServer } from "./serverComs";
import { getCurrentProject } from "./sessionManager";
import { selectInstrumentIDs } from "./store";
import { staticSelect } from "./utils/react";

class MidiHandler {
    notesActivades = new Set();
    boundInstrumentID = undefined;
    constructor() {
        this.typingInput = new TypingMIDIInput(this);
        this.externalDeviceManager = new ExternalDeviceMidiManager(this);
    }

    get isWebMidiEnabled() {
        return this.externalDeviceManager.isWebMidiEnabled;
    }

    onNoteOn(pitch, velocity) {
        if (this.boundInstrumentID) this.sendNoteOn(this.boundInstrumentID, pitch, velocity);
    }

    onNoteOff(pitch, velocity) {
        if (this.boundInstrumentID) this.sendNoteOff(this.boundInstrumentID, pitch, velocity);
    }

    bindToInstrument(instrumentID) {
        // treu les notes penjades a l'instrument anterior que no han rebut noteoff
        if (this.boundInstrumentID) {
            this.notesActivades.forEach(pitch => this.sendNoteOff(this.boundInstrumentID, pitch, 0, {force: true}));
        }
        this.boundInstrumentID = instrumentID;
    }

    // called from the panic method on audioEngine
    clearNotes() {
        this.notesActivades.clear();
    }

    sendNoteOn(instrumentID, pitch, velocity) {
        pitch = getCurrentProject().getInstrument(instrumentID).adjustPitchToInstrumentRange(pitch);
        const messageData =  {
            pitch,
            velocity,
            type: 'noteOn',
        }
        this.notesActivades.add(pitch);
        const doSendToServer = !getAudioGraphInstance().usesAudioEngine();
        this.sendMidiEvent(instrumentID, messageData, doSendToServer);
    }

    sendNoteOff(instrumentID, pitch, velocity, extras={}) {
        pitch = getCurrentProject().getInstrument(instrumentID).adjustPitchToInstrumentRange(pitch);
        const messageData =  {
            pitch,
            velocity,
            type: 'noteOff',
            ...extras
        }
        this.notesActivades.delete(pitch);
        this.sendMidiEvent(instrumentID, messageData, !getAudioGraphInstance().usesAudioEngine());
    }

    sendMidiEvent(instrumentID, data) {
        sendMessageToServer('midi_event', {instrument_id: instrumentID, midi_event_data: data});
    }


    receiveMidiEventFromServer(instrumentID, data) {
        // If a instrumentID is provided, only send to the instrument with that id. Otherwise send to all instruments
        const targetStationsIds = instrumentID ? [instrumentID] : staticSelect(selectInstrumentIDs);
        targetStationsIds.forEach(instrumentID => {
            const {pitch, velocity, type, ...extras} = data;
            getCurrentProject().getInstrument(instrumentID).onMidiNote(pitch, velocity, type === 'noteOff', {...extras, skipRecording: false});
        });
        
        data.source = "user";
        this.dispatchBrowserMidiEvent(instrumentID, data);
    }
    
    dispatchBrowserMidiEvent(instrumentID, data) {
        //Aquest event s'utilitza en el piano roll per dibuixar els requadres sobre les notes que s'estan tocant
        // i en el grid de pads del sampler per seleccionar el pad de l'última nota que s'ha tocat
        // i en el ADSR graph per marcar l'envolupant de l'última nota
        const event = new CustomEvent("midiNote-" + instrumentID, { detail: data });
        document.dispatchEvent(event);
    }
}

class Log {
    #entries = [];
    push(data) {
        const timestamp = new Date().toISOString().slice(11,19); // HH:mm:ss in UTC
        this.entries.push({timestamp, data});
        this.listeners.forEach(callback => callback());
    }
    get entries() {
        return this.#entries;
    }
    listeners = [];
    addListener(callback) {
        this.listeners.push(callback)
    }
    removeListener(callback) {
        this.listeners = this.listeners.filter(fn => fn !== callback);
    }
}

class ExternalDeviceMidiManager {
    webMidiStates = {
        NOT_ALLOWED: "not_allowed",
        ALLOWED: "allowed",
        DENIED: "denied",
        NOT_SUPPORTED: "not_supported",
    }
    webMidiState = this.webMidiStates.NOT_ALLOWED;
    midi = undefined;
    log = new Log();
    constructor(midiHandler) {
        this.midiHandler = midiHandler;
        if (!navigator.requestMIDIAccess) {
            console.error("Navigator doesn't allow/support requestMIDIAccess");
            this.webMidiStates = this.webMidiStates.NOT_SUPPORTED;
        }
        // if webmidi was enabled previously (save and check in localStorage)
        // we could do requestMIDIaccess so that users don't need to activate it in every refresh.
    }

    get isWebMidiEnabled() {
        return this.webMidiState === this.webMidiStates.ALLOWED;
    }

    init(device, callback) {
        this.requestMIDIAccess(callback, callback).then(() => this.bindToInput(device));
    }

    requestMIDIAccess(onsuccess=()=>{}, onerror=()=>{}) {
        return navigator.requestMIDIAccess().then((midiAccess) => {
            this.webMidiState = this.webMidiStates.ALLOWED;
            this.midi = midiAccess;
            onsuccess()
            }).catch((e) => {
                this.webMidiState = this.webMidiStates.DENIED;
                onerror()
        });
    }

    getAvailableMidiInputNames() {
        return [...this.midi.inputs.values()].map(input => input.name);
    }

    getMidiInputByName(midiInputName) {
        return [...this.midi.inputs.values()].find(input => input.name === midiInputName);
    }

    bindToInput(midiInputName) {
        this.unbindFromAllInputs(); // Unbind possibly existing callbacks on other devices
        if (midiInputName == null) return;
        const input = this.getMidiInputByName(midiInputName);
        input.onmidimessage = (message) => {
            this.log.push({
                device: midiInputName,
                data: message.data
            });
            // https://midi.org/expanded-midi-1-0-messages-list
            const [statusByte, ...dataBytes] = message.data;
            if (0x80 <= statusByte && statusByte < 0xf0) { // I was feeling all hexy and bitwise
                const _channel = (statusByte & 0xf) + 1; // We're ignoring it for now
                const functionType = (statusByte >> 4) - 8;
                if (functionType === 0) this.midiHandler.onNoteOff(...dataBytes);
                if (functionType === 1) {
                    const velocity = dataBytes[1];
                    if (velocity === 0) this.midiHandler.onNoteOff(...dataBytes); // weird, isn't it! some controllers do this
                    else this.midiHandler.onNoteOn(...dataBytes);

                }
            }
        }
        this.boundInput = midiInputName;
    }

    unbindFromAllInputs() {
        for (const entry of this.midi.inputs) {
            const input = entry[1];
            input.onmidimessage = undefined;
        }
        this.boundInput = undefined;
    }
}

class TypingMIDIInput {
    kbMap = new Map(["a", "w", "s", "e", "d", "f", "t", "g", "y", "h", "u", "j", "k"].map((key, index) => [key, index]));
    padMap = new Map(["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map((key, index) => [key, index]));
    baseNotes = new Map();
    boundKeys = new Map();
    isEnabled = true;

    constructor(midiHandler) {
        this.midiHandler = midiHandler;

        document.addEventListener('keydown', (e) => this.onKeyDown(e));
        document.addEventListener('keyup', (e) => this.onKeyUp(e));
    }

    enable() {
        this.isEnabled = true;
    }
    disable() {
        this.isEnabled = false;
    }

    handleOctaveUp = (instrumentID) => {
        const currentBaseNote = this.baseNotes.get(instrumentID);
        if (currentBaseNote >= 100) return;
        this.baseNotes.set(instrumentID, currentBaseNote + 12);
    }
    
    handleOctaveDown = (instrumentID) => {
        const currentBaseNote = this.baseNotes.get(instrumentID);
        if (currentBaseNote < 12) return;
        this.baseNotes.set(instrumentID, currentBaseNote - 12);
    }


    isValidEvent(evt) {
        // If typing in a text input, do not trigger MIDI events from keypress
        if ((document.activeElement.tagName === "INPUT") && (document.activeElement.type === "text")) return false;
        if (evt.repeat) return false; // If repeat event, ignore it
        return true;
    }

    onKeyUp(evt) {
        if (!this.isEnabled) return;
        if (!this.isValidEvent(evt)) return;
        const key = evt.key.toLowerCase();
        if (!this.boundKeys.has(key)) return;
        const pitch = this.boundKeys.get(key);
        this.boundKeys.delete(key);
        this.midiHandler.onNoteOff(pitch, 0);
    }
    
    onKeyDown(evt) {
        if (!this.isEnabled) return;
        if (!this.isValidEvent(evt)) return;
        const key = evt.key.toLowerCase();

        const boundInstrumentID = this.midiHandler.boundInstrumentID;
        if (boundInstrumentID === undefined) return; // needs to be bound to an instrument to work
        const usableRange = getCurrentProject().getInstrument(boundInstrumentID).pitchRange.usable;

        // Octave change
        if (key == "+") this.handleOctaveUp(boundInstrumentID);
        else if (key == "-") this.handleOctaveDown(boundInstrumentID);

        else if (this.kbMap.has(key)) {
            if (!this.baseNotes.has(boundInstrumentID)) this.baseNotes.set(boundInstrumentID, 60); // default
            let pitch;
            while (true) {
                pitch = this.baseNotes.get(boundInstrumentID) + this.kbMap.get(key);
                if (pitch < usableRange.min) this.handleOctaveUp(boundInstrumentID);
                else if (pitch > usableRange.max) this.handleOctaveDown(boundInstrumentID);
                else break;
            }
            this.boundKeys.set(key, pitch);
            this.midiHandler.onNoteOn(pitch, 127);
        } else if (this.padMap.has(key)) {
            const pitch = this.padMap.get(key);
            this.boundKeys.set(key, pitch);
            this.midiHandler.onNoteOn(pitch, 127);
        }
    }
}

export const midiHandler = new MidiHandler();